import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, ArrowRight, ShieldCheck } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { PageHero } from "../components/PageHero";
import { FaqAccordion } from "../components/FaqAccordion";
import { SERVICES } from "../data/services";
import { REGIONS } from "../data/regions";
import { CONTACT, IMAGES } from "../data/site";

const CARDS = [
    { icon: Phone, title: "Call the command centre", value: CONTACT.phone, href: CONTACT.phoneHref, note: "Answered 24/7 — by people, not menus", testid: "contact-phone-card" },
    { icon: Mail, title: "Email the team", value: CONTACT.email, href: CONTACT.emailHref, note: "Free risk assessment arranged from your first email", testid: "contact-email-card" },
    { icon: MapPin, title: "Coverage", value: "46 Scottish towns & cities", href: "/locations", note: "From the Borders to Orkney and Shetland", testid: "contact-coverage-card" },
    { icon: Clock, title: "Hours", value: "24/7 — 365 days", href: "/services", note: "Static guarding, patrols, monitoring and response", testid: "contact-hours-card" },
];

const STEPS = [
    { n: "01", title: "Call or email", text: "Tell us what you're protecting and where. You'll speak directly to our command centre — no forms, no queues." },
    { n: "02", title: "Free risk assessment", text: "We survey your site in person, review your incident history and insurance conditions, and identify your real risks." },
    { n: "03", title: "Written plan, deployed", text: "You get a written specification with honest recommendations and verified reporting. Officers can be on site within 24 hours for urgent needs." },
];

const FAQS = [
    { q: "How quickly can I get a quote?", a: "Call or email with your site details and we'll arrange a free risk assessment — usually within days. Your written proposal follows the survey, with honest recommendations you can compare like-for-like." },
    { q: "Do you provide urgent or short-notice cover?", a: "Yes — for urgent requirements we aim to have SIA-licensed officers on site within 24 hours anywhere in Scotland, and often same-day in the Central Belt. Call the command centre and ask." },
    { q: "Why is there no contact form?", a: "Because security is urgent by nature. A phone call or email reaches our command centre directly — no inbox queues, no spam filters, no waiting for a callback that never comes." },
];

export default function Contact() {
    return (
        <div data-testid="contact-page">
            <Seo
                title={`Contact Xtreme Protection | 24/7 Security Services Scotland | ${CONTACT.phone}`}
                description="Contact Xtreme Protection for SIA-licensed security services across Scotland. Call +44 2046 402840 or email admin@xtremeprotection.uk — free risk assessments, 24/7 cover, no forms."
            />
            <PageHero
                image={IMAGES.cctv}
                imageAlt="Xtreme Protection 24/7 Scottish security command centre"
                kicker="Scotland Security Operations // 24/7 Command"
                title="Contact Xtreme"
                titleAccent="Protection"
                sub="No forms, no queues, no call-back promises. One phone line and one inbox, manned around the clock by the team that will actually protect your site."
                testidPrefix="contact"
            />

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="contact-cards">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                    {CARDS.map((c, i) => {
                        const inner = (
                            <>
                                <c.icon size={20} className="text-[#E53E3E] mb-4" />
                                <p className="font-mono-xp text-[10px] uppercase tracking-[0.25em] text-gray-500">{c.title}</p>
                                <p className="font-display uppercase font-bold text-xl tracking-wide text-white mt-2 group-hover:text-[#E53E3E] transition-colors">{c.value}</p>
                                <p className="text-gray-500 text-sm leading-relaxed mt-2">{c.note}</p>
                            </>
                        );
                        const cls = "group block bg-[#0F1117] hover:bg-[#161922] transition-colors duration-300 p-7 h-full";
                        return (
                            <Reveal key={c.testid} delay={i * 0.06}>
                                {c.href.startsWith("/") ? (
                                    <Link to={c.href} data-testid={c.testid} className={cls}>{inner}</Link>
                                ) : (
                                    <a href={c.href} data-testid={c.testid} className={cls}>{inner}</a>
                                )}
                            </Reveal>
                        );
                    })}
                </div>
            </section>

            <section className="bg-[#0F1117] border-y border-white/10 py-20" data-testid="contact-steps">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <p className="font-mono-xp text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-4">The Process</p>
                        <h2 className="font-display uppercase font-extrabold text-3xl sm:text-4xl tracking-wide text-white leading-none">
                            Three steps to <span className="text-[#E53E3E]">protected</span>
                        </h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-3 gap-px bg-white/10 border border-white/10 mt-10">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.08} className="bg-[#0F1117] p-7">
                                <span className="font-mono-xp text-[11px] text-[#E53E3E] tracking-widest">{s.n}</span>
                                <h3 className="font-display uppercase font-bold text-2xl tracking-wide text-white mt-3">{s.title}</h3>
                                <p className="text-gray-500 text-sm leading-relaxed mt-3">{s.text}</p>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal className="mt-10 flex flex-col sm:flex-row gap-4">
                        <a
                            href={CONTACT.phoneHref}
                            data-testid="contact-main-phone-button"
                            className="xp-glow flex items-center justify-center gap-3 bg-[#E53E3E] hover:bg-[#c93636] transition-colors duration-300 text-white font-mono-xp text-sm uppercase tracking-widest px-8 py-4"
                        >
                            <Phone size={16} /> {CONTACT.phone}
                        </a>
                        <a
                            href={CONTACT.emailHref}
                            data-testid="contact-main-email-button"
                            className="flex items-center justify-center gap-3 border border-white/25 hover:border-[#E53E3E] hover:text-[#E53E3E] transition-colors duration-300 text-white font-mono-xp text-sm uppercase tracking-widest px-8 py-4"
                        >
                            <Mail size={16} /> {CONTACT.email}
                        </a>
                    </Reveal>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 py-20" data-testid="contact-explore">
                <Reveal>
                    <h2 className="font-display uppercase font-extrabold text-3xl sm:text-4xl tracking-wide text-white leading-none">
                        Not sure what you <span className="text-[#E53E3E]">need?</span>
                    </h2>
                    <p className="text-gray-400 text-sm leading-relaxed mt-4 max-w-2xl">
                        Browse the services and regions before you call — or just call and we'll work it out together.
                    </p>
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-10">
                    {REGIONS.map((r) => (
                        <Link
                            key={r.slug}
                            to={`/regions/${r.slug}`}
                            data-testid={`contact-region-${r.slug}`}
                            className="group flex items-center justify-between bg-[#07080B] hover:bg-[#161922] transition-colors px-5 py-4"
                        >
                            <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{r.name}</span>
                            <ArrowRight size={13} className="text-gray-700 group-hover:text-[#E53E3E] group-hover:translate-x-1 transition-all shrink-0" />
                        </Link>
                    ))}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-px">
                    {SERVICES.map((s) => (
                        <Link
                            key={s.slug}
                            to={`/services/${s.slug}`}
                            data-testid={`contact-service-${s.slug}`}
                            className="group flex items-center gap-2.5 bg-[#07080B] hover:bg-[#161922] transition-colors px-4 py-3.5"
                        >
                            <ShieldCheck size={13} className="text-[#E53E3E] shrink-0" />
                            <span className="text-sm text-gray-300 group-hover:text-white transition-colors">{s.name}</span>
                        </Link>
                    ))}
                </div>
            </section>

            <section className="bg-[#0F1117] border-t border-white/10 py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <Reveal>
                        <h2 className="font-display uppercase font-extrabold text-3xl sm:text-4xl tracking-wide text-white leading-none mb-10">
                            Before you <span className="text-[#E53E3E]">call</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <FaqAccordion faqs={FAQS} testidPrefix="contact-faq" />
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
